'use client';

import { AiIcon } from '@components/ui/ai-icon';
import { FC } from 'react';
import { useTranslations } from 'next-intl';
import { useAgentsMembershipEnabled } from '@hooks/use-agents-membership';
import { useStartAgentChat } from '@hooks/use-start-agent-chat';

type AgentChatTriggerProps = {
  organization_id: string;
  className?: string;
};

/**
 * Top-bar icon mirroring `SearchTrigger.tsx`'s exact button shape -- always
 * goes straight to the organization's single agent DM (creating it, and the
 * agent itself if needed, on demand). Renders nothing when agent chat is
 * disabled for the organization, same as every other
 * `useAgentsMembershipEnabled()` consumer.
 */
const AgentChatTrigger: FC<AgentChatTriggerProps> = ({
  organization_id,
  className
}) => {
  const enabled = useAgentsMembershipEnabled();
  const t = useTranslations('common');
  const { start, loading } = useStartAgentChat(organization_id);

  if (!enabled) return null;

  return (
    <button
      type="button"
      title={t('ai-chat')}
      aria-label={t('ai-chat')}
      onClick={() => void start()}
      disabled={loading}
      className={
        className ??
        'inline-flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-md px-3 text-body-dense text-muted-foreground hover:bg-accent disabled:opacity-60'
      }
    >
      <AiIcon className="size-5" />
    </button>
  );
};

export default AgentChatTrigger;
